import { Box, Button, Divider, Typography } from '@mui/material';
import { CommonDialog, CommonDialogHeader } from '../../Dialog';
import { IAdminAuditLogDetailResponse } from '../../../types/api/response/auditlogresponse';

interface Props {
  log: IAdminAuditLogDetailResponse | null;
  isOpen: boolean;
  onClose: () => void;
}

const Field = ({
  label,
  value,
  mono = false,
}: {
  label: string;
  value: string | number | null | undefined;
  mono?: boolean;
}) => (
  <Box py={1.5}>
    <Typography variant="caption" color="text.secondary" fontWeight={600} display="block" mb={0.5}>
      {label}
    </Typography>
    <Typography
      variant="body2"
      sx={{
        wordBreak: 'break-all',
        whiteSpace: 'pre-wrap',
        fontFamily: mono ? 'monospace' : 'inherit',
        fontSize: mono ? 12 : 'inherit',
        color: value == null ? 'text.disabled' : 'inherit',
      }}
    >
      {value ?? '—'}
    </Typography>
  </Box>
);

export const AuditLogDetailDialog = ({ log, isOpen, onClose }: Props) => {
  if (!log) return null;

  return (
    <CommonDialog
      isOpen={isOpen}
      header={<CommonDialogHeader title="Audit Log Detail" onClose={onClose} />}
      body={
        <Box mt={1} sx={{ maxHeight: '65vh', overflowY: 'auto', pr: 0.5 }}>
          <Field label="Correlation ID" value={log.correlation_id} mono />
          <Divider />
          <Field
            label="Timestamp"
            value={log.timestamp ? new Date(log.timestamp).toLocaleString() : null}
          />
          <Divider />
          <Field label="User" value={log.user_id} />
          <Divider />
          <Field label="Navigation" value={log.navigation_full_path} />
          <Divider />
          <Field label="IP Address" value={log.ip_address} />
          <Divider />
          <Field label="HTTP Method" value={log.http_method} />
          <Divider />
          <Field label="URL Path" value={log.url_path} mono />
          <Divider />
          <Field label="Query String" value={log.query_string} mono />
          <Divider />
          <Field label="Status Code" value={log.status_code} />
          <Divider />
          <Field label="Execution Time (ms)" value={log.execution_time_ms} />
          {log.error_message && (
            <>
              <Divider />
              <Field label="Error Message" value={log.error_message} />
            </>
          )}
          {log.request_body && (
            <>
              <Divider />
              <Field label="Request Body" value={log.request_body} mono />
            </>
          )}
          {log.response_body && (
            <>
              <Divider />
              <Field label="Response Body" value={log.response_body} mono />
            </>
          )}
        </Box>
      }
      footer={
        <Box display="flex" justifyContent="flex-end" mt={1}>
          <Button variant="outlined" size="small" onClick={onClose}>
            Close
          </Button>
        </Box>
      }
    />
  );
};
